import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  XCircle,
  ArrowRight,
  ArrowLeft,
  Zap,
  Battery,
  Settings,
  Eye,
  Wrench,
  Shield,
  RefreshCw,
  Home,
  CloudLightning,
  ArrowUpRight,
} from 'lucide-react';
import { useConfigWizard, WizardPhase } from '@/hooks/useConfigWizard';
import { SystemProfile, BUILT_IN_PRESETS } from '@/lib/solark-recommendations';

interface ConfigWizardProps {
  systemId: string;
  onComplete?: () => void;
  onCancel?: () => void;
}

const PHASE_CONFIG: Record<WizardPhase, { label: string; icon: React.ComponentType<{ className?: string }>; description: string }> = {
  discovery: { label: 'Discovery', icon: Eye, description: 'Finding your inverters' },
  snapshot: { label: 'Snapshot', icon: Settings, description: 'Reading current settings' },
  configure: { label: 'Configure', icon: Wrench, description: 'Set your preferences' },
  diff: { label: 'Review', icon: Eye, description: 'Review changes' },
  apply: { label: 'Apply', icon: Zap, description: 'Writing settings' },
  verify: { label: 'Verify', icon: Shield, description: 'Confirming changes' },
};

const PHASES_ORDER: WizardPhase[] = ['discovery', 'snapshot', 'configure', 'diff', 'apply', 'verify'];

const PRESET_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Zap,
  ArrowUpRight,
  Home,
  CloudLightning,
  Battery,
};

export function ConfigWizard({ systemId, onComplete, onCancel }: ConfigWizardProps) {
  const wizard = useConfigWizard({ systemId, onComplete });
  
  const currentPhaseIndex = PHASES_ORDER.indexOf(wizard.phase);
  const progressPercent = ((currentPhaseIndex + 1) / PHASES_ORDER.length) * 100;
  
  const canGoBack = currentPhaseIndex > 0 && wizard.phase !== 'apply' && wizard.phase !== 'verify';
  
  const handleBack = () => {
    const prevPhase = PHASES_ORDER[currentPhaseIndex - 1];
    if (prevPhase) {
      wizard.setPhase(prevPhase);
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress Header */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Inverter Configuration Wizard</h2>
          <Badge variant="outline">
            Step {currentPhaseIndex + 1} of {PHASES_ORDER.length}
          </Badge>
        </div>
        
        <Progress value={progressPercent} className="h-2" />
        
        <div className="flex justify-between">
          {PHASES_ORDER.map((phase, i) => {
            const config = PHASE_CONFIG[phase];
            const Icon = config.icon;
            const isActive = i === currentPhaseIndex;
            const isComplete = i < currentPhaseIndex;
            
            return (
              <div 
                key={phase}
                className={`flex flex-col items-center gap-1 ${
                  isActive ? 'text-primary' : isComplete ? 'text-muted-foreground' : 'text-muted-foreground/50'
                }`}
              >
                <div className={`p-2 rounded-full ${
                  isActive ? 'bg-primary/10' : isComplete ? 'bg-muted' : ''
                }`}>
                  {isComplete ? (
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                  ) : (
                    <Icon className="w-4 h-4" />
                  )}
                </div>
                <span className="text-xs font-medium">{config.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Error Display */}
      {wizard.error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{wizard.error}</AlertDescription>
        </Alert>
      )}

      {/* Phase Content */}
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {(() => {
              const Icon = PHASE_CONFIG[wizard.phase].icon;
              return <Icon className="w-5 h-5 text-primary" />;
            })()}
            {PHASE_CONFIG[wizard.phase].label}
          </CardTitle>
          <CardDescription>{PHASE_CONFIG[wizard.phase].description}</CardDescription>
        </CardHeader>
        
        <CardContent>
          {wizard.phase === 'discovery' && (
            <DiscoveryPhase wizard={wizard} />
          )}
          
          {wizard.phase === 'snapshot' && (
            <SnapshotPhase wizard={wizard} />
          )}
          
          {wizard.phase === 'configure' && (
            <ConfigurePhase wizard={wizard} />
          )}
          
          {wizard.phase === 'diff' && (
            <DiffPhase wizard={wizard} />
          )}
          
          {wizard.phase === 'apply' && (
            <ApplyPhase wizard={wizard} />
          )}
          
          {wizard.phase === 'verify' && (
            <VerifyPhase wizard={wizard} onComplete={onComplete} />
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <div className="flex gap-2">
          {canGoBack && (
            <Button variant="outline" onClick={handleBack} disabled={wizard.loading}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
          {onCancel && wizard.phase !== 'verify' && (
            <Button variant="ghost" onClick={onCancel} disabled={wizard.loading}>
              Cancel
            </Button>
          )}
        </div>
        
        {wizard.phase !== 'verify' && wizard.phase !== 'apply' && wizard.phase !== 'snapshot' && (
          <Button 
            onClick={wizard.nextPhase} 
            disabled={wizard.loading || (wizard.phase === 'discovery' && wizard.selectedInverters.length === 0)}
          >
            {wizard.loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ArrowRight className="w-4 h-4 mr-2" />
            )}
            {wizard.phase === 'diff' ? 'Apply Changes' : 'Continue'}
          </Button>
        )}
      </div>
    </div>
  );
}

// Phase Components

function DiscoveryPhase({ wizard }: { wizard: ReturnType<typeof useConfigWizard> }) {
  if (wizard.loading) {
    return (
      <div className="flex flex-col items-center justify-center py-8 gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Discovering inverters...</p>
      </div>
    );
  }
  
  if (wizard.inverters.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground mb-4">No inverters found</p>
        <Button onClick={wizard.discoverInverters}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry Discovery
        </Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Select the inverters you want to configure:
      </p>
      
      <div className="space-y-2">
        {wizard.inverters.map(inv => (
          <div 
            key={inv.sn}
            className="flex items-center justify-between p-4 rounded-lg border bg-wt-bg-tertiary"
          >
            <div className="flex items-center gap-3">
              <Checkbox
                checked={wizard.selectedInverters.includes(inv.sn)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    wizard.setSelectedInverters([...wizard.selectedInverters, inv.sn]);
                  } else {
                    wizard.setSelectedInverters(wizard.selectedInverters.filter(s => s !== inv.sn));
                  }
                }}
              />
              <div>
                <p className="font-medium">{inv.alias}</p>
                <p className="text-xs text-muted-foreground">SN: {inv.sn}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Badge variant={inv.role === 'primary' ? 'default' : 'secondary'}>
                {inv.role}
              </Badge>
              <Badge variant={inv.isOnline ? 'default' : 'destructive'}>
                {inv.isOnline ? 'Online' : 'Offline'}
              </Badge>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SnapshotPhase({ wizard }: { wizard: ReturnType<typeof useConfigWizard> }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 gap-4">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
      <p className="text-muted-foreground">Reading current inverter settings...</p>
      <p className="text-xs text-muted-foreground">
        This may take a moment for each inverter
      </p>
    </div>
  );
}

function ConfigurePhase({ wizard }: { wizard: ReturnType<typeof useConfigWizard> }) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  return (
    <ScrollArea className="h-[400px] pr-4">
      <div className="space-y-6">
        {/* Presets */}
        <div className="space-y-3">
          <Label className="text-base font-medium">Quick Presets</Label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(BUILT_IN_PRESETS).map(([key, preset]) => {
              const Icon = PRESET_ICONS[preset.icon] || Zap;
              const isSelected = wizard.selectedPreset === key;
              
              return (
                <button
                  key={key}
                  onClick={() => wizard.applyPreset(key)}
                  className={`p-4 rounded-lg border text-left transition-all ${
                    isSelected 
                      ? 'border-primary bg-primary/5 ring-1 ring-primary' 
                      : 'border-border hover:border-primary/50 bg-wt-bg-tertiary'
                  }`}
                >
                  <Icon className={`w-5 h-5 mb-2 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                  <p className="font-medium text-sm">{preset.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">{preset.description}</p>
                </button>
              );
            })}
          </div>
        </div>
        
        <Separator />
        
        {/* Battery Configuration */}
        <div className="space-y-4">
          <Label className="text-base font-medium">Battery Configuration</Label>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="batteryType">Battery Type</Label>
              <Select 
                value={wizard.systemProfile.batteryType}
                onValueChange={(v) => wizard.setSystemProfile({ 
                  ...wizard.systemProfile, 
                  batteryType: v as SystemProfile['batteryType'] 
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lithium">Lithium (LiFePO4)</SelectItem>
                  <SelectItem value="lead-acid">Lead Acid</SelectItem>
                  <SelectItem value="agm">AGM</SelectItem>
                  <SelectItem value="gel">Gel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="batteryVoltage">System Voltage</Label>
              <Select 
                value={String(wizard.systemProfile.batteryVoltage)}
                onValueChange={(v) => wizard.setSystemProfile({ 
                  ...wizard.systemProfile, 
                  batteryVoltage: Number(v) as SystemProfile['batteryVoltage'] 
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="48">48V</SelectItem>
                  <SelectItem value="24">24V</SelectItem>
                  <SelectItem value="12">12V</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="batteryCapacity">Capacity per Battery (Ah)</Label>
              <Input
                type="number"
                value={wizard.systemProfile.batteryCapacityAh}
                onChange={(e) => wizard.setSystemProfile({ 
                  ...wizard.systemProfile, 
                  batteryCapacityAh: Number(e.target.value) 
                })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="batteryCount">Number of Batteries</Label>
              <Input
                type="number"
                min={1}
                value={wizard.systemProfile.batteryCount}
                onChange={(e) => wizard.setSystemProfile({ 
                  ...wizard.systemProfile, 
                  batteryCount: Number(e.target.value) 
                })}
              />
            </div>
          </div>
          
          <div className="space-y-3">
            <Label>Reserve Threshold: {wizard.systemProfile.reservePercent}%</Label>
            <Slider
              value={[wizard.systemProfile.reservePercent]}
              onValueChange={([v]) => wizard.setSystemProfile({ 
                ...wizard.systemProfile, 
                reservePercent: v 
              })}
              min={5}
              max={50}
              step={5}
            />
            <p className="text-xs text-muted-foreground">
              System will shut down when battery reaches this level
            </p>
          </div>
        </div>
        
        <Separator />
        
        {/* Generator Configuration */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-base font-medium">Generator</Label>
            <Switch
              checked={wizard.systemProfile.hasGenerator}
              onCheckedChange={(checked) => wizard.setSystemProfile({ 
                ...wizard.systemProfile, 
                hasGenerator: checked 
              })}
            />
          </div>
          
          {wizard.systemProfile.hasGenerator && (
            <div className="flex items-center justify-between pl-4">
              <Label>Auto-start Generator</Label>
              <Switch
                checked={wizard.systemProfile.generatorAutoStart}
                onCheckedChange={(checked) => wizard.setSystemProfile({ 
                  ...wizard.systemProfile, 
                  generatorAutoStart: checked 
                })}
              />
            </div>
          )}
        </div>
        
        <Separator />
        
        {/* Grid Configuration */}
        <div className="space-y-4">
          <Label className="text-base font-medium">Grid Configuration</Label>
          
          <div className="flex items-center justify-between">
            <Label>Grid-Tied System</Label>
            <Switch
              checked={wizard.systemProfile.gridTied}
              onCheckedChange={(checked) => wizard.setSystemProfile({ 
                ...wizard.systemProfile, 
                gridTied: checked 
              })}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <Label>Zero Export (No grid sell-back)</Label>
            <Switch
              checked={wizard.systemProfile.zeroExport}
              onCheckedChange={(checked) => wizard.setSystemProfile({ 
                ...wizard.systemProfile, 
                zeroExport: checked 
              })}
            />
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}

function DiffPhase({ wizard }: { wizard: ReturnType<typeof useConfigWizard> }) {
  const totalChanges = Array.from(wizard.inverterSettings.values())
    .reduce((sum, state) => sum + state.diff.length, 0);
  
  if (totalChanges === 0) {
    return (
      <div className="text-center py-8">
        <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-4" />
        <p className="text-lg font-medium">No Changes Needed</p>
        <p className="text-muted-foreground">Your inverters are already configured optimally!</p>
      </div>
    );
  }
  
  return (
    <ScrollArea className="h-[400px] pr-4">
      <div className="space-y-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Review the following changes before applying. <strong>{totalChanges}</strong> settings will be modified.
          </AlertDescription>
        </Alert>
        
        {Array.from(wizard.inverterSettings.entries()).map(([sn, state]) => {
          if (!wizard.selectedInverters.includes(sn) || state.diff.length === 0) return null;
          
          return (
            <div key={sn} className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant={state.role === 'primary' ? 'default' : 'secondary'}>
                  {state.role}
                </Badge>
                <span className="font-medium">{state.alias}</span>
                <span className="text-xs text-muted-foreground">({sn})</span>
              </div>
              
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-2 font-medium">Setting</th>
                      <th className="text-left p-2 font-medium">Current</th>
                      <th className="text-left p-2 font-medium">New Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.diff.map(d => (
                      <tr key={d.field} className="border-t">
                        <td className="p-2 text-muted-foreground">
                          {wizard.SETTINGS_LABELS[d.field] || d.field}
                        </td>
                        <td className="p-2 text-destructive">
                          {wizard.formatSettingValue(d.field, d.current)}
                        </td>
                        <td className="p-2 text-primary font-medium">
                          {wizard.formatSettingValue(d.field, d.recommended)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}

function ApplyPhase({ wizard }: { wizard: ReturnType<typeof useConfigWizard> }) {
  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center justify-center py-4 gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Applying settings to inverters...</p>
      </div>
      
      <div className="space-y-2">
        {Array.from(wizard.inverterSettings.entries()).map(([sn, state]) => {
          if (!wizard.selectedInverters.includes(sn)) return null;
          
          const result = state.writeResult;
          
          return (
            <div 
              key={sn}
              className="flex items-center justify-between p-3 rounded-lg border bg-wt-bg-tertiary"
            >
              <div className="flex items-center gap-2">
                <Badge variant={state.role === 'primary' ? 'default' : 'secondary'} className="text-xs">
                  {state.role}
                </Badge>
                <span className="text-sm">{state.alias}</span>
              </div>
              
              {result === null ? (
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              ) : result.success ? (
                <CheckCircle2 className="w-4 h-4 text-primary" />
              ) : (
                <XCircle className="w-4 h-4 text-destructive" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VerifyPhase({ wizard, onComplete }: { wizard: ReturnType<typeof useConfigWizard>; onComplete?: () => void }) {
  const status = wizard.getVerificationStatus();
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center justify-center py-4 gap-4">
        {status.allVerified ? (
          <>
            <CheckCircle2 className="w-12 h-12 text-primary" />
            <div className="text-center">
              <p className="text-lg font-medium">Configuration Complete!</p>
              <p className="text-muted-foreground">
                Successfully updated {status.writtenFields} settings
              </p>
            </div>
          </>
        ) : (
          <>
            <AlertCircle className="w-12 h-12 text-wt-amber-primary" />
            <div className="text-center">
              <p className="text-lg font-medium">Partial Success</p>
              <p className="text-muted-foreground">
                Some settings may not have been applied correctly
              </p>
            </div>
          </>
        )}
      </div>
      
      <div className="space-y-2">
        {Array.from(wizard.inverterSettings.entries()).map(([sn, state]) => {
          if (!wizard.selectedInverters.includes(sn)) return null;
          
          const result = state.writeResult;
          if (!result) return null;
          
          return (
            <div 
              key={sn}
              className="flex items-center justify-between p-3 rounded-lg border bg-wt-bg-tertiary"
            >
              <div className="flex items-center gap-2">
                <Badge variant={state.role === 'primary' ? 'default' : 'secondary'} className="text-xs">
                  {state.role}
                </Badge>
                <span className="text-sm">{state.alias}</span>
                <span className="text-xs text-muted-foreground">
                  ({result.fieldsWritten.length} fields)
                </span>
              </div>
              
              {result.verificationPassed ? (
                <Badge variant="default" className="bg-primary">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Verified
                </Badge>
              ) : (
                <Badge variant="destructive">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Check Required
                </Badge>
              )}
            </div>
          );
        })}
      </div>
      
      <div className="flex justify-center">
        <Button onClick={onComplete}>
          <CheckCircle2 className="w-4 h-4 mr-2" />
          Done
        </Button>
      </div>
    </div>
  );
}

export default ConfigWizard;
